/**
 * Validation utilities for Timeline events.
 */

import {
  VALID_PRECISIONS,
  VALID_DATING_BASIS,
  VALID_CATEGORIES,
  VALID_DEPTHS
} from '../constants.js';

/**
 * Validates that a year is not 0.
 * Years transition directly from -1 (1 BC) to 1 (1 AD).
 * @param {number} year - The year to validate
 * @returns {boolean} false if year is 0, true otherwise
 */
export function validateYear(year) {
  return year !== 0;
}

/**
 * Validates that a value exists in an array of allowed values.
 * @param {*} value - The value to check
 * @param {Array} allowedValues - Array of allowed values
 * @returns {boolean} true if value is in allowedValues
 */
export function validateEnum(value, allowedValues) {
  return allowedValues.includes(value);
}

/**
 * Validates a timeline event object.
 * Checks required fields, year-0 rejection, and enum validation.
 * @param {object} event - The event object to validate
 * @returns {{ valid: boolean, errors: string[] }} Validation result
 */
export function validateEvent(event) {
  const errors = [];

  // Check required fields
  if (!event.title || (typeof event.title === 'string' && event.title.trim() === '')) {
    errors.push('Title is required');
  }

  if (event.startYear === undefined || event.startYear === null) {
    errors.push('Start year is required');
  } else if (typeof event.startYear !== 'number' || !Number.isFinite(event.startYear)) {
    errors.push('Start year must be a valid number');
  } else if (!validateYear(event.startYear)) {
    errors.push('Start year cannot be 0 — years transition from 1 BC to 1 AD');
  }

  // Validate endYear if provided
  if (event.endYear !== undefined && event.endYear !== null) {
    if (typeof event.endYear !== 'number' || !Number.isFinite(event.endYear)) {
      errors.push('End year must be a valid number');
    } else if (!validateYear(event.endYear)) {
      errors.push('End year cannot be 0 — years transition from 1 BC to 1 AD');
    }
  }

  // Validate precision if provided
  if (event.precision !== undefined && event.precision !== null) {
    if (!validateEnum(event.precision, VALID_PRECISIONS)) {
      errors.push(`Invalid precision "${event.precision}". Must be one of: ${VALID_PRECISIONS.join(', ')}`);
    }
  }

  // Validate dating_basis if provided
  if (event.dating_basis !== undefined && event.dating_basis !== null) {
    if (!validateEnum(event.dating_basis, VALID_DATING_BASIS)) {
      errors.push(`Invalid dating_basis "${event.dating_basis}". Must be one of: ${VALID_DATING_BASIS.join(', ')}`);
    }
  }

  // Validate category if provided
  if (event.category !== undefined && event.category !== null) {
    if (!validateEnum(event.category, VALID_CATEGORIES)) {
      errors.push(`Invalid category "${event.category}". Must be one of: ${VALID_CATEGORIES.join(', ')}`);
    }
  }

  // Validate depth if provided
  if (event.depth !== undefined && event.depth !== null) {
    if (!validateEnum(event.depth, VALID_DEPTHS)) {
      errors.push(`Invalid depth "${event.depth}". Must be one of: ${VALID_DEPTHS.join(', ')}`);
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}
